import {
  ComponentProps,
  ReactElement,
  ReactNode,
  useId,
  useMemo,
  useState,
} from 'react';
import * as RadixTabs from '@radix-ui/react-tabs';
import { AnimatePresence, LayoutGroup } from 'motion/react';

import { cn } from '@components/primitives/utilities';

import { TabsContext } from './Context';
import { Tab } from './Tab';
import { Trigger } from './Trigger';

import css from './styles.module.css';

type TabInstance = ReactElement<ComponentProps<typeof Tab>>;
type TabsProps = RadixTabs.TabsProps & {
  children: TabInstance | TabInstance[];
  size?: 'sm' | 'md';
  className?: string;
  variant?: 'primary' | 'secondary';
  suffix?: ReactNode;
};

function Tabs({
  children,
  className,
  defaultValue,
  value,
  onValueChange,
  size = 'md',
  variant = 'primary',
  suffix,
  ...props
}: TabsProps) {
  const id = useId();
  const childrenArray = Array.isArray(children) ? children : [children];
  const filteredChildren = childrenArray.filter(Boolean);
  const [internalValue, setValue] = useState(
    defaultValue ?? filteredChildren[0].props.label
  );

  const context = useMemo(() => {
    return { value: value || internalValue };
  }, [internalValue, value]);

  return (
    <TabsContext.Provider value={context}>
      <LayoutGroup id={id}>
        <RadixTabs.Root
          value={value || internalValue}
          onValueChange={onValueChange || setValue}
          {...props}
        >
          <RadixTabs.List
            className={css.list}
            data-size={size}
            data-variant={variant}
          >
            <AnimatePresence>
              <div className={cn(css.tabs, className)}>
                {filteredChildren.map((child) => (
                  <Trigger
                    key={child.props.label}
                    label={child.props.label}
                    icon={child.props.icon}
                  />
                ))}

                {suffix && <div className={css.suffix}>{suffix}</div>}
              </div>
            </AnimatePresence>
          </RadixTabs.List>
          <AnimatePresence mode="wait" initial={false}>
            <div>{children}</div>
          </AnimatePresence>
        </RadixTabs.Root>
      </LayoutGroup>
    </TabsContext.Provider>
  );
}

const TabsNamespace = Object.assign(Tabs, { Tab });
export { TabsNamespace as Tabs };
